import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { detectCadence } from './cadence.js'
import type { CadenceTag } from './cadence.js'
import { detectModalSignature } from './modal-signature.js'
import type { ModalSignatureHit } from './modal-signature.js'
import { matchProgression } from './progression-match.js'

/**
 * Caller-supplied window context. v1 only consumes `phraseBoundary`; `style`
 * is reserved for Milestone 5+ where blues / jazz progression matching will
 * gate library entries.
 */
export interface WindowContext {
  /** True when the *last* chord in `chords` ends a phrase. Required for any
   *  cadence to fire in v1 (the spec gates all cadence rendering on
   *  end-of-line). */
  phraseBoundary?: boolean
  style?: 'blues' | 'jazz'
}

export interface AnalyzeWindowInput {
  chords: Chord[]
  scale: Scale | undefined
  context?: WindowContext
}

/**
 * Named-progression match over the last 4–8 chords. Wired in Milestone 5.
 */
export interface ProgressionTag {
  id: string
  span: [number, number]
  lexiconId: string
}

/**
 * Window-level analysis output: cadence + progression + modal facts. Three
 * fields keep the seam explicit for downstream consumers — the UI renders
 * one chip slot at end-of-line, with cadence winning over progression on
 * collision (S2 respects this).
 *
 * `modal` is an array (one entry per qualifying chord in the window) because
 * S3a glow fires on the individual chord chip — not on the end-of-line tag.
 * A phrase may carry the characteristic note on multiple chords, and each
 * deserves its own glow. Bowie's recommendation, captured at Milestone 6.
 */
export interface ChordWindowAnalysis {
  cadence: CadenceTag | null
  progression: ProgressionTag | null
  modal: ModalSignatureHit[]
}

/**
 * Compute window-level theory facts over a chord sequence. Pure function —
 * no React state, memoisation handled at call sites by React Compiler.
 *
 * Cadence is gated on `phraseBoundary` (v1 line-end semantics — Joni §2.1).
 * Progression is NOT gated on the phrase boundary: a progression may run
 * over a phrase, and the consumer (`WindowTag`) decides whether to render
 * the chip based on placement. When both cadence and progression fire at the
 * same line end, the consumer prefers cadence (the more local, stronger
 * pedagogical signal — Joni §2.6).
 *
 * Modal facts are per-chord (not window-level) — `modal` returns one entry
 * per chord that carries the mode's characteristic note. The consumer routes
 * each entry to the matching chord chip for the S3a glow treatment.
 */
export const analyzeWindow = ({ chords, scale, context }: AnalyzeWindowInput): ChordWindowAnalysis => {
  const phraseBoundary = context?.phraseBoundary ?? false
  return {
    cadence: detectCadence(chords, scale, phraseBoundary),
    progression: matchProgression(chords, scale),
    modal: detectModalSignature(chords, scale),
  }
}
